import {
  FormItem,
  FormClear,
  FormControl,
  FormItemProps,
  FormLabel,
  FormMessage,
  FormClearProps,
  useFormFieldValue,
} from "@/components/ui/form";
import { cn, isDefined } from "@/lib/utils";
import React from "react";

export type FormItemContentProps = {
  label?: string;
  children?: React.ReactNode;
  /** Muted note under the control, e.g. where a placeholder's default value came from. */
  hint?: React.ReactNode;
  right?: React.FC;
  clear?: React.FC | null;
  clearProps?: FormClearProps;
  sideBySide?: boolean;
  size?: "sm" | "md";
  inline?: boolean;
  id?: string;
  minimal?: boolean;
} & FormItemProps;

function Minimal({
  minimal,
  children,
}: {
  minimal?: boolean;
  children?: React.ReactNode;
}) {
  const value = useFormFieldValue();
  if (!minimal) return children;
  if (isDefined(value)) {
    return children;
  }
  return undefined;
}

export function FormItemContent({
  label,
  children,
  hint,
  className,
  sideBySide,
  size,
  clear: Clear,
  clearProps,
  right: Right,
  inline,
  minimal,
  id,
  ...container
}: FormItemContentProps) {
  // const tooltipProps = useTooltipProps();
  const Wrap = Right
    ? ({ children }: { children?: React.ReactNode }) => (
        <div className="flex items-center gap-2">{children}</div>
      )
    : React.Fragment;
  return (
    <Minimal minimal={minimal}>
      <FormItem className={cn("space-y-1", className)} {...container}>
        <Wrap>
          {label !== undefined && !inline && (
            <FormLabel
              className={cn(
                size === "sm"
                  ? "text-xs absolute -top-2 left-1 z-10"
                  : undefined,
              )}
              htmlFor={id}
            >
              {label}
            </FormLabel>
          )}
          {Right !== undefined && <Right />}
        </Wrap>
        <div className="flex items-center gap-2">
          <FormControl>{children}</FormControl>
          {Clear && !inline ? (
            <Clear {...(clearProps as any)} />
          ) : Clear !== null && !inline ? (
            <FormClear
              {...clearProps}
              resetTarget={clearProps?.resetTarget ?? undefined}
              isResettable={
                clearProps?.isResettable ?? ((v) => v !== undefined)
              }
            />
          ) : (
            <></>
          )}
        </div>
        {hint !== undefined && (
          <span className="text-xs text-gray-500">{hint}</span>
        )}
        <FormMessage />
      </FormItem>
    </Minimal>
  );
}

export type FormItemCommonProps = {
  label?: FormItemContentProps["label"];
  container?: Omit<FormItemContentProps, "label" | "children">;
};
